import { WEBSITE_URL } from "../configs/website";
import { protectUserAgent, handleUserAgentError } from "./userAgentProtection";

// Initialize userAgent protection
protectUserAgent();

const websiteBase = () => String(WEBSITE_URL || "").replace(/\/+$/, "");

/** Hostinger (and APP_URL ending in /public) need /public/storage and /public/assets. */
const needsPublicBeforeStorage = (urlOrBase = "") => {
  const base = websiteBase();
  const haystack = `${base} ${urlOrBase}`;
  return /\/public$/i.test(base) || /hostingersite\.com/i.test(haystack);
};

/**
 * Force …/public/storage/… and …/public/assets/… when bare paths would 404 on Hostinger.
 * Idempotent if /public/… is already present.
 */
const ensurePublicAssetPrefix = (url) => {
  if (!url || typeof url !== "string") {
    return url;
  }

  const rewriteAbsolute = (absoluteUrl) => {
    if (/\/public\/(storage|assets)\//i.test(absoluteUrl)) {
      return absoluteUrl;
    }
    if (!needsPublicBeforeStorage(absoluteUrl)) {
      return absoluteUrl;
    }
    return absoluteUrl
      .replace(/(https?:\/\/[^/]+)\/storage\//i, "$1/public/storage/")
      .replace(/(https?:\/\/[^/]+)\/assets\//i, "$1/public/assets/");
  };

  // Absolute: https://host/storage|assets/x → https://host/public/storage|assets/x
  if (/^https?:\/\//i.test(url)) {
    return rewriteAbsolute(url);
  }

  // Relative paths
  if (needsPublicBeforeStorage()) {
    if (
      url.startsWith("/public/storage/") ||
      url.startsWith("public/storage/") ||
      url.startsWith("/public/assets/") ||
      url.startsWith("public/assets/")
    ) {
      return url.startsWith("/") ? url : `/${url}`;
    }
    if (url.startsWith("/storage/")) {
      return `/public${url}`;
    }
    if (url.startsWith("storage/")) {
      return `/public/${url}`;
    }
    if (url.startsWith("/assets/")) {
      return `/public${url}`;
    }
    if (url.startsWith("assets/")) {
      return `/public/${url}`;
    }
  }

  return url;
};

/**
 * Build a browser-reachable image URL.
 * On Hostinger, delivery proofs must be /public/storage/… (bare /storage/… → 422).
 */
export const getImageUrl = (imagePath) => {
  try {
    if (!imagePath) {
      return "";
    }

    let normalizedPath = String(imagePath).trim();
    const base = websiteBase();
    const usePublicStorage = needsPublicBeforeStorage(normalizedPath);

    // Local-only: strip accidental /public/storage when docroot is already public/
    if (!usePublicStorage) {
      normalizedPath = normalizedPath.replace(/\/public\/storage\//g, "/storage/");
      if (normalizedPath.startsWith("public/storage/")) {
        normalizedPath = `/${normalizedPath.slice("public/".length)}`;
      }
    }

    // Absolute URL from API — repair Hostinger bare /storage/
    if (/^https?:\/\//i.test(normalizedPath)) {
      return ensurePublicAssetPrefix(normalizedPath);
    }

    if (normalizedPath.includes("categories/placeholder.jpg")) {
      return "/assets/img/various/shop-placeholder.jpeg";
    }

    // Normalize relative storage paths before joining with WEBSITE_URL
    normalizedPath = ensurePublicAssetPrefix(normalizedPath);

    let cleanPath = normalizedPath.startsWith("/")
      ? normalizedPath.slice(1)
      : normalizedPath;

    // Avoid …/public/public/storage when base already ends with /public
    if (/\/public$/i.test(base) && cleanPath.startsWith("public/")) {
      cleanPath = cleanPath.slice("public/".length);
    }

    let versionParam = "";
    if (cleanPath.includes("?v=")) {
      const parts = cleanPath.split("?v=");
      cleanPath = parts[0];
      versionParam = `?v=${parts[1]}`;
    }

    return ensurePublicAssetPrefix(`${base}/${cleanPath}${versionParam}`);
  } catch (error) {
    if (handleUserAgentError(error)) {
      return imagePath || "";
    }

    console.warn("Error in getImageUrl:", error.message);
    return imagePath || "";
  }
};
